import * as React from 'react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Joyride, { STATUS, EVENTS } from 'react-joyride'
import { useContextSelector } from 'use-context-selector'
import { AppTourContext } from './@context'
import { useTourLocale } from './@hooks'

const joyrideStyles = {
    options: {
        primaryColor: '#2474b2',
        zIndex: 15000,
        arrowColor: '#fff',
        backgroundColor: '#fff',
        textColor: '#333',
        overlayColor: 'rgba(0, 0, 0, 0.5)',
    },
    overlay: {
        zIndex: 14000,
    },
    spotlight: {
        zIndex: 14500,
    },
    tooltip: {
        borderRadius: '8px',
        padding: '16px 20px',
        maxWidth: '380px',
        zIndex: 15000,
    },
    tooltipContent: {
        fontSize: '14px',
        lineHeight: '1.5',
        padding: '8px 0',
    },
    buttonNext: {
        backgroundColor: '#2474b2',
        borderRadius: '4px',
        fontSize: '13px',
    },
    buttonBack: {
        color: '#2474b2',
        fontSize: '13px',
    },
    buttonSkip: {
        color: '#999',
        fontSize: '12px',
    },
}

export default function AppTour() {
    const isRunning = useContextSelector(AppTourContext, ({ state }) => state.isRunning)
    const stepIndex = useContextSelector(AppTourContext, ({ state }) => state.stepIndex)
    const rawSteps = useContextSelector(AppTourContext, ({ state }) => state.steps)
    const updateState = useContextSelector(AppTourContext, ({ updateState }) => updateState)
    const { joyrideLocale } = useTourLocale()
    const { t } = useTranslation('tour')

    // Step texts are resolved here (not in the step builders) so they follow language changes live.
    const steps = useMemo(() => (rawSteps || []).map(({ contentKey, ...step }) =>
        contentKey ? { ...step, content: t(contentKey) } : step
    ), [rawSteps, t])

    const handleCallback = useCallback(data => {
        const { status, type, action, index } = data

        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            const currentStep = steps[index]
            if (currentStep?.data?.cleanup) currentStep.data.cleanup()
            updateState({ isRunning: false, stepIndex: 0 })
            return
        }

        if (type === EVENTS.STEP_AFTER) {
            const currentStep = steps[index]
            const nextIndex = index + (action === 'prev' ? -1 : 1)

            if (action === 'next' && currentStep?.data?.beforeNext) {
                currentStep.data.beforeNext()
                setTimeout(() => updateState({ stepIndex: nextIndex }), currentStep.data?.delay || 500)
                return
            }

            if (action === 'prev' && currentStep?.data?.beforePrev) {
                currentStep.data.beforePrev()
                setTimeout(() => updateState({ stepIndex: nextIndex }), currentStep.data?.delay || 400)
                return
            }

            updateState({ stepIndex: nextIndex })
        }
    }, [updateState, steps])

    if (!isRunning || !steps?.length) return null

    return <Joyride
        steps={steps}
        stepIndex={stepIndex}
        run={isRunning}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        scrollOffset={120}
        disableOverlayClose
        spotlightPadding={4}
        locale={joyrideLocale}
        styles={joyrideStyles}
        callback={handleCallback}
        floaterProps={{ disableAnimation: true }}
    />
}
